/** Google Analytics 4.
 *
 *  Measurement, never behaviour: every function here is safe to call at any time and never throws,
 *  so a blocked or missing gtag.js can't take a screen down with it.
 *
 *  Where it sends:
 *    - production hosts  -> real gtag.js
 *    - preview / local   -> console only, so QA traffic never lands in the client's reports
 *  Set `localStorage.ppcp_ga_force = "1"` to exercise the real gtag path from preview.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

const isPreviewHost = () => {
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".preview.emergentagent.com") ||
    host.endsWith(".emergentagent.com")
  );
};

const forced = () => {
  try {
    return window.localStorage.getItem("ppcp_ga_force") === "1";
  } catch {
    return false;
  }
};

/** True only when events should reach Google. */
export const gaEnabled = () => !!MEASUREMENT_ID && (!isPreviewHost() || forced());

let loaded = false;

// gtag.js only interprets dataLayer entries that are `arguments` objects, never plain arrays,
// so this has to stay a classic function pushing `arguments` verbatim.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

/** Loads gtag.js once. Called on first use, so nothing is fetched when analytics is off. */
const load = () => {
  if (loaded || !gaEnabled()) return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  // Route changes are sent explicitly by the app, so the automatic one would double-count.
  gtag("config", MEASUREMENT_ID, { send_page_view: false });
};

/** Send one event. Unknown/undefined params are dropped so GA4 reports stay clean. */
export const track = (event, params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
  if (!gaEnabled()) {
    console.debug("[ga4]", event, clean);
    return;
  }
  load();
  gtag("event", event, clean);
};

/** A page view for a client-side route change. */
export const trackPageView = (path, title) =>
  track("page_view", { page_path: path, page_location: window.location.href, page_title: title });

/** Ties events to the WordPress account — an id only, never an email or a name. */
export const setUserId = (id) => {
  if (!gaEnabled()) {
    console.debug("[ga4] set user_id", id);
    return;
  }
  load();
  gtag("config", MEASUREMENT_ID, { user_id: String(id), send_page_view: false });
};
